import { useState, useEffect } from 'react';
import { CalendarCardSize, CalendarCardSizeFull } from '../components/PipelineCalendar/const';


/* 此hooks主要完成三个功能
1、根据当前浏览器窗口宽度，决定当前尺寸型号，默认为'M'，
2、handleBiggerSize，放大，在当前尺寸上加1个型号，最大为 XL = 5,
3、handleSmallerSize，缩小，在当前尺寸上减1个型号，最大为 XS = 1,
4、reset，重置为默认尺寸
浏览器宽度browserWith,尺寸范围对应型号：
  小于1440: XS
  1440 - 1620: S
  1620 - 1820: M
  1820 - 1920: L
  大于1920: XL
*/
type TCardSize = CalendarCardSize | CalendarCardSizeFull;

export default function useScalable(props: {
  // 缩放比例集合，int[]，外传传入需要注意缩放比例升序排序
  sizes: TCardSize[];
  onSizeChange?: (size: TCardSize) => void;
  isFullScreen?: boolean;
}) {
  const { sizes, onSizeChange, isFullScreen = false } = props;

  /**
   * 根据浏览器窗口宽度获取对应的尺寸型号
   */
  const getSizeByWindowWidth = (): CalendarCardSize | CalendarCardSizeFull => {
    const browserWidth = window.innerWidth;
    if (isFullScreen) {
      if (browserWidth <= 1440) return CalendarCardSizeFull.XXS;
      if (browserWidth > 1440 && browserWidth < 1540) return CalendarCardSizeFull.XS;
      if (browserWidth >= 1540 && browserWidth < 1640) return CalendarCardSizeFull.S;
      if (browserWidth >= 1640 && browserWidth < 1740) return CalendarCardSizeFull.M;
      if (browserWidth >= 1740 && browserWidth < 1840) return CalendarCardSizeFull.L;
      if (browserWidth >= 1840 && browserWidth < 1920) return CalendarCardSizeFull.XL;
      if (browserWidth >= 1920 && browserWidth < 1980) return CalendarCardSizeFull.XXL;
      if (browserWidth >= 1980 && browserWidth < 2060) return CalendarCardSizeFull.XXXL;
      if (browserWidth >= 2060 && browserWidth < 2160) return CalendarCardSizeFull.XXXXL;
      if (browserWidth >= 2160 && browserWidth < 2260) return CalendarCardSizeFull.XXXXXL;
      if (browserWidth >= 2260 && browserWidth < 2360) return CalendarCardSizeFull.XXXXXXL;
      if (browserWidth >= 2360 && browserWidth < 2460) return CalendarCardSizeFull.XXXXXXXL;
      if (browserWidth >= 2460 && browserWidth < 2560) return CalendarCardSizeFull.XXXXXXXXL;
      if (browserWidth >= 2560) return CalendarCardSizeFull.XXXXXXXXXL;
    } else {
      if (browserWidth < 1440) return CalendarCardSize.XS;
      if (browserWidth >= 1440 && browserWidth < 1620) return CalendarCardSize.S;
      if (browserWidth >= 1620 && browserWidth < 1820) return CalendarCardSize.M;
      if (browserWidth >= 1820 && browserWidth < 1920) return CalendarCardSize.L;
      if (browserWidth >= 1920) return CalendarCardSize.XL;
    }
    return CalendarCardSize.M;
  };

  // 缩放比例，初始根据浏览器窗口宽度决定
  const [size, setSize] = useState<TCardSize>(getSizeByWindowWidth());

  /**
   * 全屏状态变化时，重置为对应尺寸
   */
  useEffect(() => {
    const defaultSize = getSizeByWindowWidth();
    setSize(defaultSize);
    onSizeChange?.(defaultSize);
  }, [isFullScreen]);

  /**
   * 监听窗口大小变化，自动调整尺寸
   */
  useEffect(() => {
    const handleResize = () => {
      const newSize = getSizeByWindowWidth();
      setSize(newSize);
      onSizeChange?.(newSize);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [onSizeChange, isFullScreen]);

  const check = (): { flag: boolean; index: number } => {
    if (!Array.isArray(sizes) || !sizes.length) return { flag: false, index: -1 };
    const sizeIdx = sizes.findIndex((s) => s === size);
    return { flag: sizeIdx !== -1, index: sizeIdx };
  };

  const changeSizeByIndex = (getNewSizeIdx: (currentSizeIdx: number) => number) => {
    const checkRes = check();
    if (!checkRes.flag) return;
    const sizeIdxNew = getNewSizeIdx(checkRes.index);
    const sizeNew = sizes[sizeIdxNew];
    setSize(sizeNew);
    onSizeChange?.(sizeNew);
  };

  // 点击放大
  const handleBiggerSize = () => {
    changeSizeByIndex((currentSizeIdx) => {
      // 放大，当前size的索引 + 1
      const newSizeIdx = currentSizeIdx + 1;
      // 确保索引不越界（超过最大索引）
      return Math.min(newSizeIdx, sizes.length - 1);
    });
  };

  // 点击缩小
  const handleSmallerSize = () => {
    changeSizeByIndex((currentSizeIdx) => {
      // 缩小，当前size的索引 - 1
      const newSizeIdx = currentSizeIdx - 1;
      // 确保索引不越界（小于最小索引0）
      return Math.max(newSizeIdx, 0);
    });
  };

  // 重置为默认尺寸（根据当前浏览器窗口宽度）
  const reset = () => {
    const defaultSize = getSizeByWindowWidth();
    setSize(defaultSize);
    onSizeChange?.(defaultSize);
  };

  return { size, onSmallerSize: handleSmallerSize, onBiggerSize: handleBiggerSize, reset };
}
